/**
 * @file ページの上の書き込みを読み、書き足しを間引いて保存する hook である。
 *
 * @what: (家族, まなぶ人, 資料, ページ) が変わるたびに線を読み直す。線を足したら1.5秒後に保存し、
 *   ページを送るときと unmount では待たずに保存する。保存の状態 (保存中 / 保存済み / 失敗) を返し、
 *   保存できたときは、そのページと線の本数を onSaved に渡す。
 *   ページ画像の縦横比が分かったら、線の無い文書の高さをその比に合わせる。
 *   解き直しのために、いまの線を空にして待たずに保存する入口 (clearNow) と、書きかけを待たずに
 *   保存して結果を返す入口 (saveNow。AI に渡す前に使う) も持つ。
 * @why: 書くたびに送ると多すぎ、閉じるときだけだと失うので、間引きと即時の両方を持つ。
 * @failure: 読みの失敗は空の文書として扱い、状態に「失敗」を出す (書けるが保存も失敗しうる)。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { loadPageInk, pageInkPath, savePageInk } from "../lib/pageInk";
import { HANDWRITING_WIDTH, emptyHandwriting, type HandwritingDoc } from "../model/handwriting";
import { useInkHistory } from "./useInkHistory";

/** 線を足してから保存するまでの待ち (ms)。 */
const SAVE_DELAY_MS = 1500;

export type PageInkStatus = "idle" | "loading" | "saving" | "saved" | "error";

export function usePageInk(
  familyId: string,
  learnerId: string | null,
  sourceId: string,
  page: number | null,
  /** 保存できたときに、そのページと線の本数を受け取る (書き込みのあるページの印に使う)。 */
  onSaved?: (page: number, strokes: number) => void,
) {
  const ink = useInkHistory();
  const [status, setStatus] = useState<PageInkStatus>("idle");
  const path = learnerId !== null && page !== null ? pageInkPath(familyId, learnerId, sourceId, page) : null;
  const pathRef = useRef(path);
  // 保存できたページは、いま開いているページとは限らない (送る直前の保存が後から終わる)。
  // ※なぜなら保存の相手を「いまのページ」と決めうちにすると、送った先のページの印を
  //   前のページの結果で書き換えてしまうためである。
  const pageRef = useRef(page);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const docRef = useRef(ink.doc);
  const changedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  docRef.current = ink.doc;
  changedRef.current = ink.changed;

  const flush = useCallback(async (targetPath: string, targetPage: number, doc: HandwritingDoc): Promise<boolean> => {
    setStatus("saving");
    try {
      await savePageInk(targetPath, doc);
      setStatus("saved");
      onSavedRef.current?.(targetPage, doc.strokes.length);
      return true;
    } catch (err) {
      console.error("[study] failed to save page ink", err);
      setStatus("error");
      return false;
    }
  }, []);

  // ページ (パス) が変わったら、前のページの書きかけを待たずに保存し、新しいページの線を読む。
  useEffect(() => {
    const prevPath = pathRef.current;
    const prevPage = pageRef.current;
    if (prevPath !== null && prevPage !== null && prevPath !== path && changedRef.current) {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      void flush(prevPath, prevPage, docRef.current);
    }
    pathRef.current = path;
    pageRef.current = page;
    // 読み終わるまで前のページの線を新しいページに重ねない (先に空にしてから読む)。
    ink.reset(emptyHandwriting());
    if (path === null) {
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    loadPageInk(path)
      .then((doc) => {
        if (cancelled) return;
        ink.reset(doc ?? emptyHandwriting());
        setStatus("idle");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("[study] failed to load page ink", err);
        ink.reset(emptyHandwriting());
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // ink.reset は安定している (useCallback)。path だけを見る。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, flush]);

  // 線が変わったら間引いて保存する。
  useEffect(() => {
    if (!ink.changed || path === null) return;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      ink.markSaved();
      if (page !== null) void flush(path, page, docRef.current);
    }, SAVE_DELAY_MS);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ink.doc, ink.changed, path, flush]);

  // unmount では待たずに保存する。
  useEffect(() => {
    return () => {
      const p = pathRef.current;
      if (p !== null && changedRef.current) {
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        void savePageInk(p, docRef.current).catch((err: unknown) => {
          console.error("[study] failed to save page ink on leave", err);
        });
      }
    };
  }, []);

  /**
   * いまのページの線を空にして、待たずに保存する (ファイルは消える)。解き直しで、いまの線を
   * 過去の回として残したあとに呼ぶ。書きかけの間引きの待ちは捨てる。※なぜなら待ちが後から
   * 発火すると、空にする前の線で上書きしてしまうためである。
   */
  const clearNow = useCallback(async () => {
    const p = pathRef.current;
    const targetPage = pageRef.current;
    if (p === null || targetPage === null) return;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const empty = { ...docRef.current, strokes: [] };
    ink.reset(empty);
    await flush(p, targetPage, empty);
    // ink.reset は安定している (useCallback)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flush]);

  /**
   * 書きかけがあれば待たずに保存し、保存できた (または保存するものが無い) かを返す。
   * AI に紙面を渡す前に呼ぶ。※なぜならサーバは Storage の線を重ねるので、間引きの待ちの間に
   * 押すと、書いたばかりの線が渡らないためである。
   */
  const saveNow = useCallback(async (): Promise<boolean> => {
    const p = pathRef.current;
    const targetPage = pageRef.current;
    if (p === null || targetPage === null) return false;
    if (!changedRef.current) return true;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    ink.markSaved();
    return flush(p, targetPage, docRef.current);
    // ink.markSaved は安定している (useCallback)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flush]);

  /** ページ画像の縦横比が分かったとき、線の無い文書の高さを合わせる。 */
  const setAspect = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      if (naturalWidth <= 0 || naturalHeight <= 0) return;
      const height = Math.round((HANDWRITING_WIDTH * naturalHeight) / naturalWidth);
      const d = docRef.current;
      if (d.strokes.length === 0 && d.height !== height) {
        ink.reset({ ...d, width: HANDWRITING_WIDTH, height });
      }
    },
    [ink],
  );

  return { ...ink, status, setAspect, clearNow, saveNow, enabled: path !== null };
}
